import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Save, AlertCircle } from 'lucide-react';
import { usePersonalActions } from '../../../hooks/usePersonalActions';
import { departmentsAPI, positionsAPI } from '../../../api/services';
import { contractsAPI } from '../../../api/contracts';
import { shiftsAPI } from '../../../api/shifts';

export default function PersonalActionForm({ action, employees, tipos, onSave, onCancel }) {
  const isEditing = !!action;
  const { createAction, updateAction, loading } = usePersonalActions();

  // Estados del formulario
  const [formData, setFormData] = useState({
    employee_id: '',
    tipo_accion: '',
    fecha_efectiva: '',
    fecha_fin: '',
    titulo: '',
    descripcion: '',
    documento: null,
    datos_accion: {}
  });

  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [errors, setErrors] = useState({});

  // Cargar datos del formulario si está editando
  useEffect(() => {
    if (isEditing && action) {
      setFormData({
        employee_id: action.employee_id || '',
        tipo_accion: action.tipo_accion || '',
        fecha_efectiva: action.fecha_efectiva || '',
        fecha_fin: action.fecha_fin || '',
        titulo: action.titulo || '',
        descripcion: action.descripcion || '',
        documento: null,
        datos_accion: action.datos_accion || {}
      });

      const tipo = tipos.find(t => t.codigo === action.tipo_accion);
      setSelectedTipo(tipo);
    }
  }, [action, isEditing, tipos]);

  // Cargar departamentos y cargos
  useEffect(() => {
    loadDepartmentsAndPositions();
  }, []);

  const loadDepartmentsAndPositions = async () => {
    try {
      const [deptsResponse, posResponse, shiftsResponse] = await Promise.all([
        departmentsAPI.getAll(),
        positionsAPI.getAll(),
        shiftsAPI.getAll({ activo: true })
      ]);

      const depts = Array.isArray(deptsResponse) ? deptsResponse : (deptsResponse?.data || deptsResponse?.departments || []);
      const pos = Array.isArray(posResponse) ? posResponse : (posResponse?.data || posResponse?.positions || []);
      const shiftsList = Array.isArray(shiftsResponse) ? shiftsResponse : (shiftsResponse?.shifts || shiftsResponse?.data || []);

      setDepartments(depts);
      setPositions(pos);
      setShifts(shiftsList);
    } catch (error) {
      console.error('❌ Error loading data:', error);
    }
  };

  // Cargar datos actuales del empleado cuando se selecciona
  useEffect(() => {
    if (!formData.employee_id || employees.length === 0) return;

    const selectedEmployee = employees.find(emp => emp.id === formData.employee_id);
    if (!selectedEmployee) return;

    // Cargar cargo actual
    const cargoActual = selectedEmployee.cargo || selectedEmployee.cargo_nombre;
    if (cargoActual) {
      setFormData(prev => ({
        ...prev,
        datos_accion: {
          ...prev.datos_accion,
          cargo_anterior: cargoActual,
          position_id_anterior: selectedEmployee.position_id
        }
      }));
    }

    // Cargar departamento actual
    const departamentoActual = selectedEmployee.departamento || selectedEmployee.departamento_nombre;
    if (departamentoActual) {
      setFormData(prev => ({
        ...prev,
        datos_accion: {
          ...prev.datos_accion,
          departamento_anterior: departamentoActual,
          department_id_anterior: selectedEmployee.department_id
        }
      }));
    }

    // Cargar datos del contrato activo (salario y turno)
    const loadContractData = async () => {
      try {
        const result = await contractsAPI.getAll({ employeeId: formData.employee_id, estado: 'activo' });
        const activeContract = result.contracts?.[0];
        if (activeContract) {
          setFormData(prev => ({
            ...prev,
            datos_accion: {
              ...prev.datos_accion,
              ...(activeContract.salario && {
                salario_anterior: parseFloat(activeContract.salario),
                moneda_anterior: activeContract.moneda || 'DOP'
              }),
              ...(activeContract.shift_id && {
                turno_anterior_id: activeContract.shift_id,
                turno_anterior: activeContract.turno || ''
              })
            }
          }));
        }
      } catch (e) {
        console.error('Error cargando contrato activo:', e);
      }
    };
    loadContractData();
  }, [formData.employee_id, employees]);

  // Manejar cambio de tipo de acción
  const handleTipoChange = (e) => {
    const codigo = e.target.value;
    setFormData({ ...formData, tipo_accion: codigo });

    const tipo = tipos.find(t => t.codigo === codigo);
    setSelectedTipo(tipo);
  };

  // Manejar cambios en campos básicos
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Manejar cambios en datos_accion
  const handleDatosChange = (key, value) => {
    setFormData({
      ...formData,
      datos_accion: {
        ...formData.datos_accion,
        [key]: value
      }
    });
  };

  // Manejar archivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      alert('El archivo no puede superar los 10MB');
      return;
    }
    setFormData({ ...formData, documento: file });
  };

  // Validar formulario
  const validate = () => {
    const newErrors = {};

    if (!formData.employee_id) newErrors.employee_id = 'Seleccione un empleado';
    if (!formData.tipo_accion) newErrors.tipo_accion = 'Seleccione un tipo de acción';
    if (!formData.titulo) newErrors.titulo = 'El título es requerido';
    if (!formData.fecha_efectiva) newErrors.fecha_efectiva = 'La fecha efectiva es requerida';

    if (selectedTipo?.requiere_documento && !isEditing && !formData.documento) {
      newErrors.documento = 'Este tipo de acción requiere documento adjunto';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      alert('Por favor corrija los errores en el formulario');
      return;
    }

    try {
      let result;
      if (isEditing) {
        result = await updateAction(action.id, formData);
      } else {
        result = await createAction(formData);
      }

      if (result.success) {
        alert(isEditing ? 'Acción actualizada exitosamente' : 'Acción creada exitosamente');
        onSave();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error saving action:', error);
      alert('Error al guardar acción');
    }
  };

  // Renderizar campos específicos según tipo de acción
  const renderDatosAccionFields = () => {
    if (!selectedTipo) return null;

    const { codigo } = selectedTipo;

    // Campos para Ascenso / Cambio de Cargo
    if (['ascenso', 'cambio_puesto'].includes(codigo)) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cargo Actual</label>
            <input
              type="text"
              value={formData.datos_accion?.cargo_anterior || 'Seleccione un empleado primero'}
              readOnly
              className="w-full px-4 py-2 border-2 rounded-xl bg-gray-50 text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cargo Nuevo <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.datos_accion?.position_id_nuevo || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                const pos = positions.find(p => p.id === selectedId);
                setFormData(prev => ({
                  ...prev,
                  datos_accion: { ...prev.datos_accion, position_id_nuevo: selectedId, cargo_nuevo: pos?.nombre || '' }
                }));
              }}
              className="w-full px-4 py-2 border-2 rounded-xl"
              required
            >
              <option value="">Seleccione cargo...</option>
              {positions.map(pos => (
                <option key={pos.id} value={pos.id}>{pos.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Salario Actual</label>
            <input
              type="text"
              value={formData.datos_accion?.salario_anterior
                ? `RD$ ${parseFloat(formData.datos_accion.salario_anterior).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`
                : 'Seleccione un empleado primero'}
              readOnly
              className="w-full px-4 py-2 border-2 rounded-xl bg-gray-50 text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Salario Nuevo (Opcional)</label>
            <input
              type="number"
              step="0.01"
              value={formData.datos_accion?.salario_nuevo || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                datos_accion: { ...prev.datos_accion, salario_nuevo: e.target.value ? parseFloat(e.target.value) : null }
              }))}
              placeholder="Dejar vacío si no cambia"
              className="w-full px-4 py-2 border-2 rounded-xl"
            />
          </div>
        </div>
      );
    }

    // Campos para Cambio de Departamento
    if (codigo === 'cambio_departamento') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Departamento Actual</label>
            <input
              type="text"
              value={formData.datos_accion?.departamento_anterior || 'Seleccione un empleado primero'}
              readOnly
              className="w-full px-4 py-2 border-2 rounded-xl bg-gray-50 text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Departamento Nuevo <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.datos_accion?.department_id_nuevo || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                const dept = departments.find(d => d.id === selectedId);
                setFormData(prev => ({
                  ...prev,
                  datos_accion: { ...prev.datos_accion, department_id_nuevo: selectedId, departamento_nuevo: dept?.nombre || '' }
                }));
              }}
              className="w-full px-4 py-2 border-2 rounded-xl"
              required
            >
              <option value="">Seleccione departamento...</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    // Campos para Cambio de Turno
    if (codigo === 'cambio_turno') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Turno Actual</label>
            <input
              type="text"
              value={formData.datos_accion?.turno_anterior || 'Seleccione un empleado primero'}
              readOnly
              className="w-full px-4 py-2 border-2 rounded-xl bg-gray-50 text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Turno Nuevo <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.datos_accion?.turno_nuevo_id || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                const shift = shifts.find(s => s.id === selectedId);
                setFormData(prev => ({
                  ...prev,
                  datos_accion: { ...prev.datos_accion, turno_nuevo_id: selectedId, turno_nuevo: shift?.nombre || '' }
                }));
              }}
              className="w-full px-4 py-2 border-2 rounded-xl"
              required
            >
              <option value="">Seleccione turno...</option>
              {shifts.map(shift => (
                <option key={shift.id} value={shift.id}>{shift.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    // Campos para Ajuste Salarial
    if (codigo === 'ajuste_salarial') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Salario Actual
            </label>
            <input
              type="text"
              value={formData.datos_accion.salario_anterior
                ? `RD$ ${parseFloat(formData.datos_accion.salario_anterior).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`
                : 'Seleccione un empleado primero'}
              readOnly
              className="w-full px-4 py-2 border-2 rounded-xl bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Salario Nuevo <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.datos_accion.salario_nuevo || ''}
              onChange={(e) => handleDatosChange('salario_nuevo', parseFloat(e.target.value))}
              className="w-full px-4 py-2 border-2 rounded-xl"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Ajuste
            </label>
            <select
              value={formData.datos_accion.tipo_ajuste || ''}
              onChange={(e) => handleDatosChange('tipo_ajuste', e.target.value)}
              className="w-full px-4 py-2 border-2 rounded-xl"
            >
              <option value="">Seleccione...</option>
              <option value="aumento_merito">Aumento por mérito</option>
              <option value="aumento_antiguedad">Aumento por antigüedad</option>
              <option value="ajuste_inflacion">Ajuste por inflación</option>
              <option value="promocion">Promoción</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Porcentaje (%)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.datos_accion.porcentaje || ''}
              onChange={(e) => handleDatosChange('porcentaje', parseFloat(e.target.value))}
              className="w-full px-4 py-2 border-2 rounded-xl"
            />
          </div>
        </div>
      );
    }

    // Campos para Suspensión
    if (codigo === 'suspension') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Suspensión
            </label>
            <select
              value={formData.datos_accion.tipo_suspension || ''}
              onChange={(e) => handleDatosChange('tipo_suspension', e.target.value)}
              className="w-full px-4 py-2 border-2 rounded-xl"
            >
              <option value="">Seleccione...</option>
              <option value="disciplinaria">Disciplinaria</option>
              <option value="medica">Médica</option>
              <option value="administrativa">Administrativa</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Días de Suspensión
            </label>
            <input
              type="number"
              value={formData.datos_accion.dias_suspension || ''}
              onChange={(e) => handleDatosChange('dias_suspension', parseInt(e.target.value))}
              className="w-full px-4 py-2 border-2 rounded-xl"
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.datos_accion.goce_salario || false}
                onChange={(e) => handleDatosChange('goce_salario', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Con goce de salario</span>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo
            </label>
            <textarea
              value={formData.datos_accion.motivo || ''}
              onChange={(e) => handleDatosChange('motivo', e.target.value)}
              className="w-full px-4 py-2 border-2 rounded-xl"
              rows="3"
            />
          </div>
        </div>
      );
    }

    // Campos para Amonestación
    if (codigo === 'amonestacion') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Amonestación
            </label>
            <select
              value={formData.datos_accion.tipo_amonestacion || ''}
              onChange={(e) => handleDatosChange('tipo_amonestacion', e.target.value)}
              className="w-full px-4 py-2 border-2 rounded-xl"
            >
              <option value="">Seleccione...</option>
              <option value="verbal">Verbal</option>
              <option value="escrita">Escrita</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gravedad
            </label>
            <select
              value={formData.datos_accion.gravedad || ''}
              onChange={(e) => handleDatosChange('gravedad', e.target.value)}
              className="w-full px-4 py-2 border-2 rounded-xl"
            >
              <option value="">Seleccione...</option>
              <option value="leve">Leve</option>
              <option value="moderada">Moderada</option>
              <option value="grave">Grave</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Faltas Previas
            </label>
            <input
              type="number"
              value={formData.datos_accion.faltas_previas || 0}
              onChange={(e) => handleDatosChange('faltas_previas', parseInt(e.target.value))}
              className="w-full px-4 py-2 border-2 rounded-xl"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">

      {/* ── Back button ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          {isEditing ? 'Volver' : 'Volver a Acciones de Personal'}
        </button>
      </div>

      {/* ── Title card ───────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl px-6 py-4">
        <h2 className="text-lg font-bold text-gray-900">
          {isEditing ? 'Editar Acción de Personal' : 'Nueva Acción de Personal'}
        </h2>
      </div>

      {/* ── Form ─────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <form onSubmit={handleSubmit}>
          {/* Información Básica */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Información Básica</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Empleado <span className="text-red-500">*</span>
                </label>
                <select
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-xl ${errors.employee_id ? 'border-red-500' : ''}`}
                  required
                  disabled={isEditing}
                >
                  <option value="">Seleccione empleado...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre} {emp.apellido} - {emp.employee_id}
                    </option>
                  ))}
                </select>
                {errors.employee_id && (
                  <p className="text-red-500 text-xs mt-1">{errors.employee_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Acción <span className="text-red-500">*</span>
                </label>
                <select
                  name="tipo_accion"
                  value={formData.tipo_accion}
                  onChange={handleTipoChange}
                  className={`w-full px-4 py-2 border-2 rounded-xl ${errors.tipo_accion ? 'border-red-500' : ''}`}
                  required
                  disabled={isEditing}
                >
                  <option value="">Seleccione tipo...</option>
                  {tipos.map(tipo => (
                    <option key={tipo.codigo} value={tipo.codigo}>
                      {tipo.nombre}
                    </option>
                  ))}
                </select>
                {errors.tipo_accion && (
                  <p className="text-red-500 text-xs mt-1">{errors.tipo_accion}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Efectiva <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="fecha_efectiva"
                  value={formData.fecha_efectiva}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-xl ${errors.fecha_efectiva ? 'border-red-500' : ''}`}
                  required
                />
                {errors.fecha_efectiva && (
                  <p className="text-red-500 text-xs mt-1">{errors.fecha_efectiva}</p>
                )}
              </div>

              {selectedTipo?.requiere_fecha_fin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    name="fecha_fin"
                    value={formData.fecha_fin}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border-2 rounded-xl"
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  placeholder="Ej: Ascenso a Gerente de Ventas"
                  className={`w-full px-4 py-2 border-2 rounded-xl ${errors.titulo ? 'border-red-500' : ''}`}
                  required
                />
                {errors.titulo && (
                  <p className="text-red-500 text-xs mt-1">{errors.titulo}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border-2 rounded-xl"
                  placeholder="Detalles de la acción..."
                />
              </div>
            </div>
          </div>

          {/* Campos Específicos según Tipo */}
          {selectedTipo && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Datos Específicos</h3>
              {renderDatosAccionFields()}
            </div>
          )}

          {/* Documento */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Documento Adjunto
              {selectedTipo?.requiere_documento && !isEditing && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h3>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl cursor-pointer hover:bg-gray-200 transition-colors">
                <Upload className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-700">
                  {formData.documento ? formData.documento.name : 'Seleccionar archivo'}
                </span>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                />
              </label>
              {errors.documento && (
                <p className="text-red-500 text-xs">{errors.documento}</p>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Formatos permitidos: PDF, DOC, DOCX, JPG, PNG (máx. 10MB)
            </p>
            {isEditing && action.documento_url && (
              <div className="mt-2">
                <a
                  href={action.documento_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm"
                >
                  Ver documento actual
                </a>
              </div>
            )}
          </div>

          {/* Alerta de impactos */}
          {selectedTipo && (selectedTipo.actualiza_contrato || selectedTipo.actualiza_estado_empleado) && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 mb-1">Esta acción aplicará los siguientes cambios:</p>
                  <ul className="text-sm text-blue-700 list-disc list-inside">
                    {selectedTipo.actualiza_contrato && (
                      <li>Actualizará el contrato activo del empleado</li>
                    )}
                    {selectedTipo.actualiza_estado_empleado && (
                      <li>Modificará el estado del empleado</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEditing ? 'Actualizar' : 'Crear Acción'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
